import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { NotificationManager } from './NotificationManager.js';

const NotificationContext = createContext();

// The visible toast list changes constantly; the callback surface never does. They are kept in
// separate contexts so a toast appearing cannot re-render every data consumer in the app.
const NotificationToastContext = createContext();

const noop = () => {};

/**
 * Access the notification callback surface: toasts, update subscriptions and presence.
 *
 * The returned object changes identity only when the underlying manager is created or torn down,
 * so it is safe to depend on in `useEffect` and `useCallback`. In particular it does not change
 * when a toast arrives — read those with `useNotificationToasts` instead.
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Access the currently visible toasts. Only the component that renders them should call this —
 * anything else will re-render on every toast.
 */
export const useNotificationToasts = () => {
  const context = useContext(NotificationToastContext);
  if (!context) {
    throw new Error('useNotificationToasts must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Global notification provider - should be placed at app root level
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [manager, setManager] = useState(null);
  const managerRef = useRef(null);

  useEffect(() => {
    const notificationManager = new NotificationManager();
    managerRef.current = notificationManager;
    setManager(notificationManager);

    const unsubscribe = notificationManager.addListener((visibleNotifications) => {
      setNotifications(visibleNotifications);
    });

    // A tab returning to the foreground only needs a reset if its connection actually died.
    // Reconnecting unconditionally is what turned tab-switching into connection churn.
    const handleVisibilityChange = () => {
      if (document.hidden || typeof window === 'undefined' || !window.reconnectUpdateStream) {
        return;
      }
      const state = notificationManager.getConnectionState();
      // `connecting` used to be treated as healthy, which made a wedged handshake invisible to
      // recovery. A handshake still within its timeout is left alone; a stalled one is replaced.
      const isMakingProgress = state === 'connecting' && !notificationManager.isHandshakeStalled();
      if (state !== 'connected' && !isMakingProgress) {
        window.reconnectUpdateStream(notificationManager);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (typeof window !== 'undefined' && window.reconnectUpdateStream) {
        delete window.reconnectUpdateStream;
      }

      notificationManager.cleanup();
      managerRef.current = null;
      setManager(null);
    };
  }, []);

  const addNotification = useCallback((documentId, message, options = {}) => {
    managerRef.current?.addNotification(documentId, message, options);
  }, []);

  const removeNotification = useCallback((documentId) => {
    managerRef.current?.removeNotification(documentId);
  }, []);

  const addDocumentUpdateCallback = useCallback(
    (callback) => managerRef.current?.addDocumentUpdateCallback(callback) ?? noop, []);

  const addViewerUpdateCallback = useCallback(
    (callback) => managerRef.current?.addViewerUpdateCallback(callback) ?? noop, []);

  const addTaskUpdateCallback = useCallback(
    (callback) => managerRef.current?.addTaskUpdateCallback(callback) ?? noop, []);

  const addTaskViewerUpdateCallback = useCallback(
    (callback) => managerRef.current?.addTaskViewerUpdateCallback(callback) ?? noop, []);

  const addReferralUpdateCallback = useCallback(
    (callback) => managerRef.current?.addReferralUpdateCallback(callback) ?? noop, []);

  const addConsultationUpdateCallback = useCallback(
    (callback) => managerRef.current?.addConsultationUpdateCallback(callback) ?? noop, []);

  const addReconnectCallback = useCallback(
    (callback) => managerRef.current?.addReconnectCallback(callback) ?? noop, []);

  const addConnectionStateCallback = useCallback(
    (callback) => managerRef.current?.addConnectionStateCallback(callback) ?? noop, []);

  const getConnectionState = useCallback(
    () => managerRef.current?.getConnectionState() ?? 'disconnected', []);

  const setViewing = useCallback((viewing) => {
    managerRef.current?.setViewing(viewing);
  }, []);

  // Every callback above is memoised on [], so this value only changes when the manager itself
  // is created or torn down — twice in the lifetime of the app. Notably it does not change when
  // a toast arrives, which is what previously re-rendered every list and detail controller.
  const contextValue = useMemo(() => ({
    addNotification,
    removeNotification,
    addDocumentUpdateCallback,
    addViewerUpdateCallback,
    addTaskUpdateCallback,
    addTaskViewerUpdateCallback,
    addReferralUpdateCallback,
    addConsultationUpdateCallback,
    addReconnectCallback,
    addConnectionStateCallback,
    getConnectionState,
    setViewing,
    manager
  }), [
    addNotification,
    removeNotification,
    addDocumentUpdateCallback,
    addViewerUpdateCallback,
    addTaskUpdateCallback,
    addTaskViewerUpdateCallback,
    addReferralUpdateCallback,
    addConsultationUpdateCallback,
    addReconnectCallback,
    addConnectionStateCallback,
    getConnectionState,
    setViewing,
    manager
  ]);

  const toastValue = useMemo(() => ({
    notifications,
    removeNotification
  }), [notifications, removeNotification]);

  return (
    <NotificationContext.Provider value={contextValue}>
      <NotificationToastContext.Provider value={toastValue}>
        {children}
      </NotificationToastContext.Provider>
    </NotificationContext.Provider>
  );
};
